import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { msalInstance } from './config/msalConfig';
import { queryClient } from './config/queryClient.ts';
import App from './App';
import './styles/index.css';
import { AppThemeProvider, ThemeModeProvider, useThemeMode, resolveTenantTheme } from './theme';
import { ChatProvider } from './contexts/ChatContext';
import { MessageProvider } from './contexts/MessageContext';
import { TenantInfoProvider, useTenantInfo } from './contexts/TenantInfoContext';
import { NProgress } from "nprogress-v2";
import "nprogress-v2/dist/index.css";

NProgress.configure({ showSpinner: false });

// Runtime-then-build-time env read (window._env_ from /env-config.js first, then
// the value baked at build). Matches the getEnv() pattern in api.resource.ts,
// config.js and agenticApiClient.js.
const getEnv = (key) =>
  (typeof window !== 'undefined' && window._env_ ? window._env_[key] : undefined) ?? process.env[key];

const ThemedApp = ({ children }) => {
  const { mode } = useThemeMode();
  const { tenantInfo } = useTenantInfo();
  // Tenant theme selection. Each tenant's frontend is built with its own
  // REACT_APP_TENANT_NAME ("sps"/"flcom"/"gdtint"…) baked in — the same
  // mechanism the live preprod/pilot SPS app uses — so the branded theme is
  // known synchronously on first render and there's no flash of the default
  // theme while /api/v1/tenant/me loads (notably on the chat page). A
  // server-provided themeColor (GET /tenant/me) still wins when present, so the
  // backend can override per-tenant later without a rebuild. Unknown/blank names
  // fall back to the default theme inside resolveTenantTheme().
  const themeName = tenantInfo?.themeColor || getEnv('REACT_APP_TENANT_NAME');
  return (
    <AppThemeProvider theme={resolveTenantTheme(themeName, mode)}>
      {children}
    </AppThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));

msalInstance.initialize().then(() => {
  root.render(
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <MsalProvider instance={msalInstance}>
          <TenantInfoProvider>
            <ThemedApp>
              <ChatProvider>
                <MessageProvider>
                  <App />
                </MessageProvider>
              </ChatProvider>
            </ThemedApp>
          </TenantInfoProvider>
        </MsalProvider>
      </ThemeModeProvider>
    </QueryClientProvider>,
  );
});
